import type { ReportResolver, ReportRow, Grain, ReportContext } from "@/lib/reports/types";
import { fetchEmployeeUserIds } from "@/hooks/reports/employee-filter";
import { fetchBillingInputs } from "@/lib/billing-query";
import {
  calcBillingRatesData,
  calcBillingRate,
  calcMonthElapsedWeekdayFraction,
  type MonthlyBillingData,
} from "@/lib/billing-rates-calc";

export interface FaktAnsattRad {
  userId: string;
  name: string;
  fakturerte: number;
  tilgjengelige: number;
  grad: number;
}

/** Ren transform — enhetstestbar uten Supabase. */
export function faktureringsgradRows(data: FaktAnsattRad[]): ReportRow[] {
  return data.map((d) => ({
    id: d.userId,
    label: d.name,
    canDrill: true,
    cells: { fakturerte: d.fakturerte, tilgjengelige: d.tilgjengelige, grad: d.grad },
  }));
}

/**
 * Ren oppsummering av en ansatts måneder til ett periode-tall.
 * Filtrerer på år-måned innenfor [from, to] (inklusiv) og summer billable/capacity.
 * Faktureringsgrad regnes med den kanoniske `calcBillingRate` over de summerte
 * timene (ikke snitt av per-måned-grad), så grad = fakturerte / tilgjengelige.
 *
 * Inneværende (uavsluttede) kalendermåned behandles spesielt for å matche den
 * autoritative dashboard-KPI-en (`teamYtdRate`, som kun teller lukkede måneder):
 * - Fullførte/historiske måneder (`monthKey < currentMonthKey`) teller ALLTID
 *   med full faktisk kapasitet.
 * - Inneværende måned (`monthKey === currentMonthKey`) tas KUN med når
 *   `includeCurrentMonth` er satt, og da pro-rates kapasiteten med samme
 *   arbeidsdag-brøk som `calcCurrentMonthAdjustedTeamRate` bruker (dashboardets
 *   «justert»-tall). Fakturerte timer hittil tas med fullt ut.
 * - Framtidige måneder (`monthKey > currentMonthKey`) teller aldri.
 */
export function summarizeMonths(
  months: MonthlyBillingData[],
  from: string,
  to: string,
  now: Date,
  includeCurrentMonth: boolean,
): { fakturerte: number; tilgjengelige: number; grad: number } {
  const fromKey = from.slice(0, 7); // yyyy-mm
  const toKey = to.slice(0, 7);
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthFraction = calcMonthElapsedWeekdayFraction(now);
  let fakturerte = 0;
  let tilgjengelige = 0;
  for (const m of months) {
    const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
    if (key < fromKey || key > toKey) continue;
    if (key > currentMonthKey) continue; // future months never count
    if (key === currentMonthKey) {
      if (!includeCurrentMonth) continue;
      // Prorate the open month's capacity (reuses the SOT elapsed-weekday
      // fraction) so numerator and denominator cover the same elapsed window.
      fakturerte += m.billableHours;
      tilgjengelige += m.capacity * monthFraction;
      continue;
    }
    // Closed/historical month — full actual capacity.
    fakturerte += m.billableHours;
    tilgjengelige += m.capacity;
  }
  return {
    fakturerte,
    tilgjengelige,
    grad: calcBillingRate(fakturerte, tilgjengelige) ?? 0,
  };
}

async function fetchFaktAnsatt(ctx: ReportContext): Promise<FaktAnsattRad[]> {
  // Faktureringsgrad-beregningen er år-bunden. ReportContext.period er et fritt
  // dato-intervall, men måned-/kvartal-/år-modusene gir alltid perioder innenfor
  // ETT kalenderår. KJENT BEGRENSNING (Fase 1, akseptert): perioder som spenner to
  // kalenderår bruker kun året fra `from` — vi krasjer ikke, men måneder i `to`-året
  // utelates til kryss-år-semantikken koordineres på rammeverk-nivå.
  const year = new Date(ctx.period.from).getFullYear();
  const now = new Date();
  const includeCurrentMonth = ctx.filters.includeCurrentMonth ?? false;

  const inputs = await fetchBillingInputs({ year });
  const { employees } = calcBillingRatesData({ ...inputs, year, now });

  // Ekskluder ikke-ansatte (statister/eksterne). billing-query filtrerer i dag
  // kun is_active, ikke role, så external aktive brukere ville ellers lekke inn.
  const employeeIds = await fetchEmployeeUserIds();

  return employees
    .filter((emp) => employeeIds.has(emp.userId))
    .map((emp) => {
      const { fakturerte, tilgjengelige, grad } = summarizeMonths(
        emp.months,
        ctx.period.from,
        ctx.period.to,
        now,
        includeCurrentMonth,
      );
      return { userId: emp.userId, name: emp.fullName, fakturerte, tilgjengelige, grad };
    });
}

export const faktureringsgradResolver: ReportResolver = {
  async fetch(level: Grain, ctx: ReportContext): Promise<ReportRow[]> {
    if (level === "ansatt") return faktureringsgradRows(await fetchFaktAnsatt(ctx));
    return [];
  },
};
